<template>
  <v-data-table
    data-private
    disable-sort
    calculate-widths
    :footer-props="footerOptions"
    :headers="headers"
    :items="items"
    :loading="isLoading"
    :options.sync="options"
    :page="currentPage"
    :server-items-length="totalItems"
  >
    <template v-slot:[`item.createdAt`]="{ item }">
      <span>{{ formatDate(item.createdAt) }}</span>
    </template>

    <template v-slot:[`item.tokens`]="{ item }">
      <span>{{ formatNumber(item.tokens) }}</span>
    </template>

    <template v-slot:[`item.createdBy`]="{ item }">
      <ChipAvatar small :account="item.createdBy" />
    </template>

    <template v-slot:[`item.trackingNumber`]="{ item }">
      <span>{{ item.trackingNumber }}</span>
    </template>
  </v-data-table>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import access from '@/lib/access'

import ChipAvatar from '@/components/ChipAvatar.vue'

const INITIAL_ITEMS_PER_PAGE = 10

const DEFAULT_LANGUAGE = 'en-US'

export default {
  name: 'TokenDepositsHistoryTable',

  mixins: [mixin],

  components: {
    ChipAvatar,
  },

  data() {
    return {
      isLoading: false,
      items: [],
      totalItems: 0,
      currentPage: 1,
      itemsPerPage: INITIAL_ITEMS_PER_PAGE,
      footerOptions: {
        itemsPerPageOptions: [10, 50, 100],
      },
    }
  },

  props: {
    organizationId: {
      type: String,
      default: null,
    },
  },

  mounted() {
    this.fetchDeposits()
  },

  computed: {
    ...mapGetters({
      account: 'account',
      currentAccountOrganizationId: 'organizationId',
    }),

    options: {
      get() {
        return {
          page: this.currentPage,
          itemsPerPage: this.itemsPerPage,
        }
      },

      set(event) {
        if (
          event.page === this.currentPage &&
          this.itemsPerPage === event.itemsPerPage
        )
          return
        this.currentPage = event.page
        this.itemsPerPage = event.itemsPerPage

        this.fetchDeposits()
      },
    },

    userLanguage() {
      return window.navigator.language || DEFAULT_LANGUAGE
    },

    isReverAIAdmin() {
      return access.isAIAdmin(this.account)
    },

    headers() {
      return [
        {
          text: this.$t('ai_features.token_deposit_history.assigned_on'),
          align: 'start',
          sortable: false,
          value: 'createdAt',
        },
        {
          text: this.$t('ai_features.token_deposit_history.assigned_tokens'),
          align: 'start',
          sortable: false,
          value: 'tokens',
        },
        this.isReverAIAdmin && {
          text: this.$t('ai_features.assigned_by'),
          align: 'start',
          sortable: false,
          value: 'createdBy',
        },
        this.isReverAIAdmin && {
          text: this.$t('ai_features.tracking_number'),
          align: 'start',
          sortable: false,
          value: 'trackingNumber',
        },
      ]
    },
  },

  methods: {
    async fetchDeposits() {
      this.isLoading = true
      try {
        const { data } = await api.ai.getDeposits(
          this.organizationId ?? this.currentAccountOrganizationId,
          {
            skip: (this.currentPage - 1) * this.itemsPerPage,
            limit: this.itemsPerPage,
          }
        )
        this.items = data.results || []
        this.totalItems = data?.totalRecordsInData

        this.isLoading = false
      } catch (error) {
        this.showError(
          this.$t('ai_features.usage.error_fetching_deposit_history')
        )
        this.isLoading = false
      }
    },

    formatDate(dateString) {
      return moment(dateString).format('LLL')
    },

    formatNumber(value) {
      const abbreviatedValue = Intl.NumberFormat(this.userLanguage, {
        maximumFractionDigits: 1,
      }).format(value)
      return abbreviatedValue
    },
  },
}
</script>
